import { create } from "zustand";
import { api, writeToken, readToken } from "./client";
import type { LoginResponse, SessionUser } from "./types";

const USER_KEY = "mentoria_user";

/**
 * Where the session can be, mapped to the backend's own gates:
 *   anonymous      no valid token
 *   must-reset     logged in with a one-time password (must_reset_password)
 *   onboarding     password set, mandatory onboarding not finished
 *   active         full access
 *
 * `onboarding` is inferred from a 403 + MUST_COMPLETE_ONBOARDING rather than
 * stored, because the flag lives inside the signed JWT and only a fresh token
 * clears it.
 */
export type SessionStage = "anonymous" | "must-reset" | "onboarding" | "active";

function readStoredUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: SessionUser | null) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    // Storage unavailable; session survives for this tab only.
  }
}

interface SessionState {
  user: SessionUser | null;
  stage: SessionStage;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  /** Called after reset-password / complete-onboarding hand back a fresh token. */
  adoptToken: (token: string) => void;
  setStage: (stage: SessionStage) => void;
  setUser: (user: SessionUser) => void;
  logout: () => void;
}

export const useSession = create<SessionState>((set, get) => ({
  user: readStoredUser(),
  stage: readToken() && readStoredUser() ? "active" : "anonymous",

  login: async (email, password, rememberMe = false) => {
    const result = await api<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password, rememberMe },
    });
    writeToken(result.token);
    writeStoredUser(result.user);
    set({ user: result.user, stage: result.mustResetPassword ? "must-reset" : "active" });
  },

  adoptToken: (token) => {
    writeToken(token);
    set({ stage: "active" });
  },

  setStage: (stage) => set({ stage }),

  setUser: (user) => {
    const merged = { ...get().user, ...user } as SessionUser;
    writeStoredUser(merged);
    set({ user: merged });
  },

  logout: () => {
    writeToken(null);
    writeStoredUser(null);
    set({ user: null, stage: "anonymous" });
  },
}));

/**
 * Which nav tree a session sees. The backend's role names are admin/manager/
 * employee; the product calls the admin surface "Admin Account" — except the
 * People-Function approver admins (Nikhar/Juhi), who are labelled "Super Admin
 * Account" to distinguish them from the primary HR & Admin account (Vyomi).
 */
export function roleLabel(account: {
  role: SessionUser["role"];
  is_people_function_approver?: boolean;
}): string {
  if (account.role === "admin") {
    return account.is_people_function_approver ? "Super Admin Account" : "Admin Account";
  }
  if (account.role === "manager") return "Manager";
  return "Employee";
}

const HR_DATES_EDITOR_EMAIL = "vyomi.bhanushali@mentoria.com";

/**
 * Whether this account can correct another person's birthday or work
 * anniversary from Org Calendar > Full Year > People. Scoped to Vyomi's
 * account only — the real enforcement is the matching check server-side in
 * PUT /api/admin/users/:id/dates; this just decides whether to show the edit
 * affordance at all.
 */
export function canEditOrgDates(user: Pick<SessionUser, "email"> | null | undefined): boolean {
  return (user?.email ?? "").toLowerCase() === HR_DATES_EDITOR_EMAIL;
}
